import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, History as HistoryIcon, Link2, Paperclip, ExternalLink, Pencil, BookOpen } from "lucide-react";
import {
  DECISION_STATUS,
  EVIDENCE_STATE,
  HYPOTHESIS_STATE,
  MEETING_TYPE,
  READING_COVERAGE,
  RESULT_OUTCOME,
  formatDateOnly,
  formatTimestamp,
} from "@brainlog/shared";
import { ApiError, errorMessage, get, put } from "@/lib/api";
import { qk, useMe } from "@/lib/queries";
import { openNote, openPaper } from "@/lib/store";
import type { ResearchRecord } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { kindLabel } from "@/features/notes/kinds";
import { StateChangeDialog } from "./StateChangeDialog";
import { RecordLinks } from "./RecordLinks";
import { ExternalRefs } from "./ExternalRefs";
import { FileLinksPanel } from "@/features/files/FileLinksPanel";
import { ReproEditor } from "./ReproEditor";
import { NotePicker, LibraryPicker } from "./pickers";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MarkdownPreview } from "@/features/notes/MarkdownPreview";

export interface RecordDetail {
  record: ResearchRecord | null;
  links: { id: string; toType: string; toId: string; relation: string; note: string | null; target: { accessible: boolean; title?: string; kind?: string; status?: string; noteId?: string; manuscriptId?: string } }[];
  incomingLinks: { id: string; relation: string; from: { id: string; title: string; kind: string } }[];
  history: {
    id: string;
    field: string;
    fromValue: string | null;
    toValue: string | null;
    effectiveDate: string | null;
    reason: string | null;
    changedAt: string;
    changedBy: string | null;
    linkedNotes: { id: string; title: string | null; kind: string | null }[];
  }[];
  externalRefs: any[];
  attachments: any[];
  plan: null | { noteId: string; title: string | null; accessible: boolean; snapshot: { id: string; versionNo: number; createdAt: string } | null; changedSinceSnapshot: boolean };
  libraryItem: null | { id: string; title: string; citationKey: string | null; mergedIntoId: string | null };
  supersededBy: null | { id: string; title: string | null };
  supersedes: { noteId: string; title: string }[];
  executions: { noteId: string; title: string; recordDate: string | null }[];
  members: { id: string; displayName: string; role: string }[];
}

const DATE_LABEL: Record<string, string> = {
  meeting: "Meeting date",
  decision: "Decision date",
  study_execution: "Run date",
  research_log: "Log date",
  result: "Result date",
  progress_note: "Note date",
};

export const STATE_FIELDS: Record<string, { field: "hypothesis_state" | "evidence_state" | "decision_status"; column: keyof ResearchRecord; labels: Record<string, string>; title: string }> = {
  hypothesis: { field: "hypothesis_state", column: "hypothesisState", labels: HYPOTHESIS_STATE.labels, title: "Hypothesis state" },
  result: { field: "evidence_state", column: "evidenceState", labels: EVIDENCE_STATE.labels, title: "Evidence state" },
  decision: { field: "decision_status", column: "decisionStatus", labels: DECISION_STATUS.labels, title: "Decision status" },
};

export function useRecordDetail(noteId: string) {
  return useQuery({ queryKey: ["record", noteId], queryFn: () => get<RecordDetail>(`/api/notes/${noteId}/record`) });
}

export function RecordPanel(props: { noteId: string; kind: string; record: ResearchRecord; workspaceId: string; readOnly?: boolean; onCreateTasks?(): void }) {
  const me = useMe();
  const qc = useQueryClient();
  const detail = useRecordDetail(props.noteId);
  const prefKey = `brainlog-claude-record-panel:${props.kind}`;
  const [open, setOpen] = useState(() => {
    // Reading notes (including in-depth notes) start collapsed: the text is what matters there.
    const byDefault = ["study_execution", "result", "meeting", "decision", "hypothesis"].includes(props.kind);
    try {
      const v = localStorage.getItem(prefKey);
      return v === null ? byDefault : v === "open";
    } catch {
      return byDefault;
    }
  });
  const [stateOpen, setStateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const rec = detail.data?.record ?? props.record;
  const members = detail.data?.members ?? [];
  const ro = !!props.readOnly;

  useEffect(() => {
    try {
      localStorage.setItem(prefKey, open ? "open" : "closed");
    } catch {
      /* ignore */
    }
  }, [open, prefKey]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["record", props.noteId] });
    qc.invalidateQueries({ queryKey: qk.note(props.noteId) });
    qc.invalidateQueries({ queryKey: qk.tree(props.workspaceId) });
  };

  const save = async (patch: Record<string, unknown>) => {
    try {
      await put(`/api/notes/${props.noteId}/record`, { version: rec.version, ...patch });
      refresh();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) toast.error("This record was changed by someone else. The latest values are shown now.");
      else toast.error(errorMessage(e));
      refresh();
    }
  };

  const stateDef = STATE_FIELDS[props.kind];
  const currentState = stateDef ? (rec[stateDef.column] as string | null) : null;
  const memberName = (id: string | null) => members.find((m) => m.id === id)?.displayName ?? (id ? "Former member" : null);

  const summary: string[] = [];
  if (stateDef) summary.push(`${stateDef.title}: ${currentState ? stateDef.labels[currentState] : "Not recorded"}`);
  if (DATE_LABEL[props.kind]) summary.push(`${DATE_LABEL[props.kind]}: ${formatDateOnly(rec.recordDate)}`);
  if (rec.responsibleUserId || rec.responsibleText) summary.push(`Responsible: ${memberName(rec.responsibleUserId) ?? rec.responsibleText}`);
  if (props.kind === "result") summary.push(`Outcome: ${rec.resultOutcome ? RESULT_OUTCOME.labels[rec.resultOutcome as keyof typeof RESULT_OUTCOME.labels] : "Not recorded"}`);
  if (props.kind === "reading_note") summary.push(`Coverage: ${rec.readingCoverage ? READING_COVERAGE.labels[rec.readingCoverage as keyof typeof READING_COVERAGE.labels] : "Not recorded"}`);

  return (
    <section className="border-b bg-sidebar/60" aria-label="Record details">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-accent/50 sm:px-6"
      >
        {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        <span className="font-medium">{kindLabel(props.kind)} details</span>
        <span className="truncate text-muted-foreground">{summary.join(" · ")}</span>
      </button>
      {open && (
        <div className="max-h-[38vh] space-y-4 overflow-y-auto px-4 pb-4 sm:px-6">
          <p className="text-xs text-muted-foreground">
            These details are kept apart from the note’s text. All of them are optional.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stateDef && (
              <div className="space-y-1">
                <Label>{stateDef.title}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-sm" data-testid="record-state">
                    {currentState ? stateDef.labels[currentState] : "Not recorded"}
                  </Badge>
                  {!ro && (
                    <Button size="xs" variant="outline" onClick={() => setStateOpen(true)}>
                      <Pencil /> Change…
                    </Button>
                  )}
                  <Button size="xs" variant="ghost" onClick={() => setHistoryOpen(!historyOpen)} aria-expanded={historyOpen}>
                    <HistoryIcon /> History ({detail.data?.history.length ?? 0})
                  </Button>
                </div>
                {props.kind === "hypothesis" && <p className="text-xs text-muted-foreground">Only you change this state. Completed tasks or assistant output never change it.</p>}
              </div>
            )}
            {DATE_LABEL[props.kind] && (
              <div className="space-y-1">
                <Label htmlFor={`rd-${props.noteId}`}>{DATE_LABEL[props.kind]}</Label>
                <Input
                  id={`rd-${props.noteId}`}
                  type="date"
                  disabled={ro}
                  defaultValue={rec.recordDate ?? ""}
                  key={`rd-${rec.version}`}
                  onBlur={(e) => e.target.value !== (rec.recordDate ?? "") && save({ recordDate: e.target.value || null })}
                  className="h-8"
                />
              </div>
            )}
            {["decision", "study_execution", "result", "study_plan"].includes(props.kind) && (
              <div className="space-y-1">
                <Label htmlFor={`resp-${props.noteId}`}>Responsible person</Label>
                <select
                  id={`resp-${props.noteId}`}
                  disabled={ro}
                  className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                  value={rec.responsibleUserId ?? ""}
                  onChange={(e) => save({ responsibleUserId: e.target.value || null })}
                >
                  <option value="">{rec.responsibleText ? `Text: ${rec.responsibleText}` : "Not recorded"}</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {props.kind === "result" && (
              <div className="space-y-1">
                <Label htmlFor={`out-${props.noteId}`}>Outcome type</Label>
                <select
                  id={`out-${props.noteId}`}
                  disabled={ro}
                  className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                  value={rec.resultOutcome ?? ""}
                  onChange={(e) => save({ resultOutcome: e.target.value || null })}
                >
                  <option value="">Not recorded</option>
                  {RESULT_OUTCOME.values.map((v) => (
                    <option key={v} value={v}>
                      {RESULT_OUTCOME.labels[v]}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">Negative, null, contradictory and failed results are valid records.</p>
              </div>
            )}
            {props.kind === "decision" && (
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor={`chosen-${props.noteId}`}>Chosen option (one line, shown in lists)</Label>
                <Input
                  id={`chosen-${props.noteId}`}
                  disabled={ro}
                  defaultValue={(rec.details?.chosenOption as string) ?? ""}
                  key={`co-${rec.version}`}
                  maxLength={500}
                  className="h-8"
                  onBlur={(e) => e.target.value !== ((rec.details?.chosenOption as string) ?? "") && save({ details: { chosenOption: e.target.value } })}
                />
                {detail.data?.supersededBy && (
                  <p className="text-sm">
                    Superseded by{" "}
                    {detail.data.supersededBy.title ? (
                      <button type="button" className="underline" onClick={() => openNote(detail.data!.supersededBy!.id, detail.data!.supersededBy!.title!)}>
                        {detail.data.supersededBy.title}
                      </button>
                    ) : (
                      "a decision you cannot access"
                    )}
                  </p>
                )}
                {detail.data?.supersedes.map((s) => (
                  <p key={s.noteId} className="text-sm">
                    Supersedes{" "}
                    <button type="button" className="underline" onClick={() => openNote(s.noteId, s.title)}>
                      {s.title}
                    </button>{" "}
                    (kept in history)
                  </p>
                ))}
              </div>
            )}
            {props.kind === "meeting" && (
              <>
                <div className="space-y-1">
                  <Label htmlFor={`mt-${props.noteId}`}>Meeting type</Label>
                  <select
                    id={`mt-${props.noteId}`}
                    disabled={ro}
                    className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                    value={rec.meetingType ?? ""}
                    onChange={(e) => save({ meetingType: e.target.value || null })}
                  >
                    <option value="">Not recorded</option>
                    {MEETING_TYPE.values.map((v) => (
                      <option key={v} value={v}>
                        {MEETING_TYPE.labels[v]}
                      </option>
                    ))}
                  </select>
                </div>
                <fieldset className="space-y-1">
                  <legend className="text-sm font-medium">Participants (members)</legend>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {members.map((m) => (
                      <label key={m.id} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          disabled={ro}
                          checked={rec.participantMemberIds.includes(m.id)}
                          onChange={(e) =>
                            save({ participantMemberIds: e.target.checked ? [...rec.participantMemberIds, m.id] : rec.participantMemberIds.filter((x) => x !== m.id) })
                          }
                        />
                        {m.displayName}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className="space-y-1">
                  <Label htmlFor={`ext-${props.noteId}`}>Other participants (names as text)</Label>
                  <Input
                    id={`ext-${props.noteId}`}
                    disabled={ro}
                    className="h-8"
                    defaultValue={rec.participantsExternal.join("; ")}
                    key={`ext-${rec.version}`}
                    onBlur={(e) => {
                      const list = e.target.value.split(";").map((s) => s.trim()).filter(Boolean);
                      if (list.join(";") !== rec.participantsExternal.join(";")) save({ participantsExternal: list });
                    }}
                  />
                  <p className="text-xs text-muted-foreground">Separate names with “;”. People named here are not invited and cannot be given tasks.</p>
                </div>
                {!ro && props.onCreateTasks && (
                  <div className="flex items-end">
                    <Button size="sm" variant="outline" onClick={props.onCreateTasks}>
                      Create tasks from action items…
                    </Button>
                  </div>
                )}
              </>
            )}
            {props.kind === "reading_note" && (
              <>
                <div className="space-y-1 sm:col-span-2">
                  <Label>Library item</Label>
                  {detail.data?.libraryItem ? (
                    <div className="flex items-center gap-2 text-sm">
                      <BookOpen className="size-4" aria-hidden />
                      <button type="button" className="truncate underline" onClick={() => openPaper(detail.data!.libraryItem!.id, detail.data!.libraryItem!.title || "Paper")}>
                        {detail.data.libraryItem.title || "Untitled source"}
                      </button>
                      {!ro && <ChangeLibraryItem workspaceId={props.workspaceId} onPick={(id) => save({ libraryItemId: id })} />}
                    </div>
                  ) : !ro ? (
                    <ChangeLibraryItem workspaceId={props.workspaceId} onPick={(id) => save({ libraryItemId: id })} label="Link to a library item" />
                  ) : (
                    <p className="text-sm text-muted-foreground">Not linked</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`cov-${props.noteId}`}>What was read</Label>
                  <select
                    id={`cov-${props.noteId}`}
                    disabled={ro}
                    className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                    value={rec.readingCoverage ?? ""}
                    onChange={(e) => save({ readingCoverage: e.target.value || null })}
                  >
                    <option value="">Not recorded</option>
                    {READING_COVERAGE.values.map((v) => (
                      <option key={v} value={v}>
                        {READING_COVERAGE.labels[v]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {props.kind === "study_execution" && (
              <div className="space-y-1 sm:col-span-2 lg:col-span-3">
                <Label>Study plan (protocol) used</Label>
                {detail.data?.plan ? (
                  <div className="space-y-1 text-sm">
                    <p>
                      {detail.data.plan.accessible ? (
                        <button type="button" className="underline" onClick={() => openNote(detail.data!.plan!.noteId, detail.data!.plan!.title ?? "Plan")}>
                          {detail.data.plan.title}
                        </button>
                      ) : (
                        "A plan you cannot access"
                      )}
                      {detail.data.plan.snapshot && (
                        <>
                          {" "}
                          — protocol snapshot v{detail.data.plan.snapshot.versionNo} recorded {formatTimestamp(detail.data.plan.snapshot.createdAt, me.data?.timezone)}.{" "}
                          <button type="button" className="underline" onClick={() => setSnapshotOpen(true)}>
                            View the protocol as used
                          </button>
                        </>
                      )}
                    </p>
                    {detail.data.plan.changedSinceSnapshot && (
                      <p className="rounded border border-warning/50 bg-warning/10 px-2 py-1 text-xs" role="status">
                        The plan has been edited since this run was recorded. This run keeps the snapshot it used.
                      </p>
                    )}
                    {!ro && <PlanPicker workspaceId={props.workspaceId} onPick={(id) => save({ planNoteId: id })} label="Change plan" />}
                  </div>
                ) : !ro ? (
                  <PlanPicker workspaceId={props.workspaceId} onPick={(id) => save({ planNoteId: id })} label="Link the study plan this run executes" />
                ) : (
                  <p className="text-sm text-muted-foreground">Not linked</p>
                )}
              </div>
            )}
            {props.kind === "study_plan" && (detail.data?.executions.length ?? 0) > 0 && (
              <div className="space-y-1 sm:col-span-2">
                <Label>Recorded runs of this plan</Label>
                <ul className="text-sm">
                  {detail.data!.executions.map((e) => (
                    <li key={e.noteId}>
                      <button type="button" className="underline" onClick={() => openNote(e.noteId, e.title)}>
                        {e.title}
                      </button>{" "}
                      <span className="text-muted-foreground">({formatDateOnly(e.recordDate)})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {historyOpen && detail.data && (
            <div>
              <h3 className="mb-1 text-sm font-medium">State history</h3>
              <ol className="space-y-1 text-sm">
                {detail.data.history.map((h) => (
                  <li key={h.id} className="rounded border bg-background px-2 py-1">
                    <span className="font-medium">{formatDateOnly(h.effectiveDate)}</span>: {h.fromValue ? `${stateDef?.labels[h.fromValue] ?? h.fromValue} → ` : ""}
                    {stateDef?.labels[h.toValue ?? ""] ?? h.toValue}
                    {h.reason && <span className="text-muted-foreground"> — {h.reason}</span>}
                    {h.linkedNotes.length > 0 && (
                      <span className="block text-xs">
                        Based on:{" "}
                        {h.linkedNotes.map((n, i) => (
                          <span key={n.id}>
                            {i > 0 && ", "}
                            {n.title ? (
                              <button type="button" className="underline" onClick={() => openNote(n.id, n.title!)}>
                                {n.title}
                              </button>
                            ) : (
                              "an inaccessible record"
                            )}
                          </span>
                        ))}
                      </span>
                    )}
                    <span className="block text-xs text-muted-foreground">
                      Recorded by {h.changedBy ?? "unknown"} on {formatTimestamp(h.changedAt, me.data?.timezone)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(props.kind === "study_execution" || props.kind === "result") && (
            <ReproEditor value={(rec.details?.repro as Record<string, string>) ?? {}} readOnly={ro} onSave={(repro) => save({ details: { repro } })} />
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                <Link2 className="size-4" aria-hidden /> Linked records
              </h3>
              {detail.data && <RecordLinks noteId={props.noteId} kind={props.kind} workspaceId={props.workspaceId} detail={detail.data} readOnly={ro} onChange={refresh} />}
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                  <ExternalLink className="size-4" aria-hidden /> External references
                </h3>
                {detail.data && <ExternalRefs ownerType="note" ownerId={props.noteId} refs={detail.data.externalRefs} readOnly={ro} onChange={refresh} />}
              </div>
              <div>
                <h3 className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                  <Paperclip className="size-4" aria-hidden /> Supporting files
                </h3>
                <FileLinksPanel
                  targetType="note"
                  targetId={props.noteId}
                  workspaceId={props.workspaceId}
                  readOnly={ro}
                  hint="For small, shareable artifacts (figures, summary tables, scripts). Keep participant-level or restricted data in your approved external systems and add an external reference instead."
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {stateOpen && stateDef && (
        <StateChangeDialog
          open
          onOpenChange={setStateOpen}
          noteId={props.noteId}
          workspaceId={props.workspaceId}
          kind={props.kind}
          field={stateDef.field}
          labels={stateDef.labels}
          current={currentState}
          onDone={refresh}
        />
      )}
      {snapshotOpen && detail.data?.plan?.snapshot && (
        <SnapshotDialog noteId={detail.data.plan.noteId} versionId={detail.data.plan.snapshot.id} onClose={() => setSnapshotOpen(false)} />
      )}
    </section>
  );
}

function ChangeLibraryItem({ workspaceId, onPick, label = "Change" }: { workspaceId: string; onPick(id: string): void; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="xs" variant="outline" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to a library item</DialogTitle>
            <DialogDescription>Only records in this project's library can be linked.</DialogDescription>
          </DialogHeader>
          <LibraryPicker
            workspaceId={workspaceId}
            onPick={(i) => {
              onPick(i.id);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function PlanPicker({ workspaceId, onPick, label }: { workspaceId: string; onPick(id: string): void; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="xs" variant="outline" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose the study plan</DialogTitle>
            <DialogDescription>A snapshot of the plan's current text is stored with this run, so later edits to the protocol do not change this run's context.</DialogDescription>
          </DialogHeader>
          <NotePicker
            workspaceId={workspaceId}
            kinds={["study_plan"]}
            onPick={(id) => {
              onPick(id);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function SnapshotDialog({ noteId, versionId, onClose }: { noteId: string; versionId: string; onClose(): void }) {
  const v = useQuery({ queryKey: ["version", noteId, versionId], queryFn: () => get<{ version: { contentMd: string; versionNo: number; title: string } }>(`/api/notes/${noteId}/versions/${versionId}`) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Protocol as used{v.data ? ` — ${v.data.version.title} (v${v.data.version.versionNo})` : ""}</DialogTitle>
          <DialogDescription>Read-only snapshot recorded with this run.</DialogDescription>
        </DialogHeader>
        {v.data ? <MarkdownPreview markdown={v.data.version.contentMd} /> : <p className="text-sm text-muted-foreground">Loading…</p>}
      </DialogContent>
    </Dialog>
  );
}
